import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Store } from "../domain/Store";
import { HttpStoreRepository } from "../application/HttpStoreRepository";
import { Delivery } from "../domain/Delivery";
import { Shopper } from "../domain/Shopper";
import { Admin } from "../domain/Admin";

const repository = new HttpStoreRepository();

export function useGetAllStores() {
  return useQuery({
    queryKey: ["stores"],
    queryFn: () => repository.getAll(),
  });
}

export function useGetStore(id?: string) {
  return useQuery({
    queryKey: ["store", id],
    queryFn: () => repository.getById(id!),
    enabled: !!id,
  });
}

export function useStore() {
  const queryClient = useQueryClient();
  const invalidateStores = () =>
    queryClient.invalidateQueries({ queryKey: ["stores"] });

  const createStore = useMutation({
    mutationFn: (store: Store) => repository.create(store),
    onSuccess: invalidateStores,
  });

  const updateStore = useMutation({
    mutationFn: ({ id, store }: { id: string; store: Store }) =>
      repository.update(id, store),
    onSuccess: invalidateStores,
  });

  const deleteStore = useMutation({
    mutationFn: (id: string) => repository.delete(id),
    onSuccess: invalidateStores,
  });

  return { createStore, updateStore, deleteStore };
}

/* ---------------- SHOPPERS ---------------- */
export function useGetShopper(id?: string) {
  return useQuery({
    queryKey: ["shopper"],
    queryFn: () => repository.getShoperId(id!),
    enabled: !!id,
  });
}

export function useGetAdmin(id?: string) {
  return useQuery({
    queryKey: ["admin"],
    queryFn: () => repository.getAdminId(id!),
    enabled: !!id,
  });
}

export function useGetShoppersByStoreId(storeId?: string) {
  return useQuery({
    queryKey: ["shoppers", "store", storeId],
    queryFn: () => repository.getShoppersByStoreId(storeId!),
    enabled: !!storeId,
  });
}

export function useShopper() {
  const queryClient = useQueryClient();
  const invalidateStores = () =>
    queryClient.invalidateQueries({ queryKey: ["shopper"] });

  const addShopper = useMutation({
    mutationFn: (shopper: Shopper) => repository.addShopper(shopper),
    onSuccess: invalidateStores,
  });

  const updateShopper = useMutation({
    mutationFn: ({ id, shopper }: { id: string; shopper: Partial<Shopper> }) =>
      repository.updateShopper(id, shopper),
    onSuccess: invalidateStores,
  });

  const deleteShopper = useMutation({
    mutationFn: (id: string) => repository.deleteShopperId(id),
    onSuccess: invalidateStores,
  });

  return { addShopper, updateShopper, deleteShopper };
}

/* ---------------- DELIVERIES ---------------- */
export function useGetDelivery(id?: string) {
  return useQuery({
    queryKey: ["delivery", id],
    queryFn: () => repository.getDeliveryId(id!),
    enabled: !!id,
  });
}

export function useDelivery() {
  const queryClient = useQueryClient();
  const invalidateStores = () =>
    queryClient.invalidateQueries({ queryKey: ["stores"] });

  const addDelivery = useMutation({
    mutationFn: (delivery: Delivery) => repository.addDelivery(delivery),
    onSuccess: invalidateStores,
  });

  const updateDelivery = useMutation({
    mutationFn: ({ id, delivery }: { id: string; delivery: Delivery }) =>
      repository.updateDelivery(id, delivery),
    onSuccess: invalidateStores,
  });

  const deleteDelivery = useMutation({
    mutationFn: (id: string) => repository.deleteDelivery(id),
    onSuccess: invalidateStores,
  });

  return { addDelivery, updateDelivery, deleteDelivery };
}
